import React, { useEffect, useState } from 'react';
import { Button } from './Button';
import { ArrowLeftIcon, ArrowRightIcon, SparklesIcon, CloudIcon, CloudRainIcon, SunIcon, ActivityIcon, BrainIcon, HeartIcon, UsersIcon, LightbulbIcon, CheckCircleIcon } from 'lucide-react';
interface CheckInSummaryProps {
  checkInData: any;
  onViewSuggestions: () => void;
  onBackToHome: () => void;
  onStartExercise: (exerciseId: string) => void;
}
const CheckInSummary: React.FC<CheckInSummaryProps> = ({
  checkInData,
  onViewSuggestions,
  onBackToHome,
  onStartExercise
}) => {
  const [animateIn, setAnimateIn] = useState(false);
  const isQuickCheckIn = checkInData?.type === 'quick';
  useEffect(() => {
    const timer = setTimeout(() => setAnimateIn(true), 100);
    return () => clearTimeout(timer);
  }, []);
  // Calculate energy and positivity from mood data
  const energy = checkInData?.mood ? 100 - checkInData.mood.y : 50; // Invert Y axis for energy
  const positivity = checkInData?.mood ? checkInData.mood.x : 50; // X axis for positivity
  const getWeatherIcon = () => {
    if (energy > 80 && positivity > 70) return <SunIcon className="w-12 h-12 text-gray-700" data-id="element-92" />;
    if (energy > 60 && positivity > 50) return <CloudIcon className="w-12 h-12 text-gray-700" data-id="element-93" />;
    if (energy < 40 && positivity < 30) return <CloudRainIcon className="w-12 h-12 text-gray-700" data-id="element-94" />;
    return <CloudIcon className="w-12 h-12 text-gray-700" data-id="element-95" />;
  };
  const getWeatherDescription = () => {
    if (energy > 80 && positivity > 70) return 'Helder met zonnige momenten';
    if (energy > 60 && positivity > 50) return 'Licht bewolkt met opklaringen';
    if (energy < 40 && positivity < 30) return 'Bewolkt met enkele buien';
    if (energy < 30 && positivity > 50) return 'Rustig en kalm';
    if (energy < 50 || positivity < 50) return 'Licht bewolkt met enkele buien';
    return 'Afwisselend bewolkt met heldere momenten';
  };
  const getCheckInInsight = () => {
    if (isQuickCheckIn) {
      if (energy > 70 && positivity > 70) return 'Je voelt je energiek en positief. Een goed moment om productieve taken aan te pakken.';
      if (energy < 40 && positivity < 40) return 'Je voelt je mogelijk wat gespannen en somber. Een korte ademhalingsoefening kan helpen.';
      return 'Je stemming is in balans. Blijf mindful gedurende de dag.';
    } else {
      // For full check-in, we have more data to work with
      const anxietyLevel = checkInData?.anxiety || 3;
      const depressionLevel = checkInData?.depression || 3;
      const focusLevel = checkInData?.focus || 3;
      const thoughtsLevel = checkInData?.thoughts || 3;
      const connectionLevel = checkInData?.connection || 3;
      const energyLevel = checkInData?.energy || 3;
      if (anxietyLevel > 3 && thoughtsLevel > 3) return 'Je ervaart wat spanning en piekeren. Mindfulness oefeningen kunnen helpen om meer in het moment te zijn.';
      if (depressionLevel > 3) return 'Je voelt je wat somber. Beweging en sociale contacten kunnen je stemming verbeteren.';
      if (focusLevel < 3) return 'Je concentratie is wat lager dan normaal. Korte focus-oefeningen kunnen helpen.';
      if (connectionLevel < 3) return 'Je voelt je wat minder verbonden met anderen. Overweeg om contact te maken met vrienden of familie.';
      if (energyLevel < 3) return 'Je energieniveau is wat lager. Een korte wandeling kan je energie verhogen.';
      return 'Je mentale balans ziet er goed uit. Blijf activiteiten doen die je goed doen.';
    }
  };
  const getRecommendedExercises = () => {
    if (energy < 40 && positivity < 40) {
      return [{
        id: 'stress-1',
        title: 'Ademhalingsoefening',
        description: 'Een korte oefening om tot rust te komen'
      }, {
        id: 'focus-1',
        title: 'Grounding techniek',
        description: 'Anker jezelf in het hier en nu'
      }];
    }
    if (energy > 80 && positivity > 70) {
      return [{
        id: 'focus-2',
        title: 'Dankbaarheidsoefening',
        description: 'Reflecteer op positieve aspecten'
      }, {
        id: 'mindful-1',
        title: 'Mindful wandelen',
        description: 'Gebruik je energie voor een mindful activiteit'
      }];
    }
    return [{
      id: 'balance-1',
      title: 'Balans meditatie',
      description: 'Vind evenwicht in je dag'
    }, {
      id: 'mindful-2',
      title: 'Mindful moment',
      description: 'Een korte pauze om te herfocussen'
    }];
  };
  const renderDomainInsights = () => {
    if (isQuickCheckIn) return null;
    return <div className="space-y-6 mb-6" data-id="element-96">
        <h3 className="text-lg font-medium text-gray-800" data-id="element-97">Domein inzichten</h3>
        <div className="grid grid-cols-2 gap-3" data-id="element-98">
          <div className="p-4 border border-gray-300 rounded-lg" data-id="element-99">
            <div className="flex items-center mb-2" data-id="element-100">
              <BrainIcon className="w-4 h-4 text-gray-600 mr-2" data-id="element-101" />
              <h4 className="text-sm font-medium text-gray-700" data-id="element-102">Emotioneel</h4>
            </div>
            <div className="space-y-2" data-id="element-103">
              <div data-id="element-104">
                <div className="flex justify-between text-xs text-gray-600 mb-1" data-id="element-105">
                  <span data-id="element-106">Angst</span>
                  <span data-id="element-107">{checkInData?.anxiety || 3}/5</span>
                </div>
                <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden" data-id="element-108">
                  <div className="h-full bg-gray-600 rounded-full" style={{
                  width: `${(checkInData?.anxiety || 3) / 5 * 100}%`
                }} data-id="element-109"></div>
                </div>
              </div>
              <div data-id="element-110">
                <div className="flex justify-between text-xs text-gray-600 mb-1" data-id="element-111">
                  <span data-id="element-112">Somberheid</span>
                  <span data-id="element-113">{checkInData?.depression || 3}/5</span>
                </div>
                <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden" data-id="element-114">
                  <div className="h-full bg-gray-600 rounded-full" style={{
                  width: `${(checkInData?.depression || 3) / 5 * 100}%`
                }} data-id="element-115"></div>
                </div>
              </div>
            </div>
          </div>
          <div className="p-4 border border-gray-300 rounded-lg" data-id="element-116">
            <div className="flex items-center mb-2" data-id="element-117">
              <HeartIcon className="w-4 h-4 text-gray-600 mr-2" data-id="element-118" />
              <h4 className="text-sm font-medium text-gray-700" data-id="element-119">Mentaal</h4>
            </div>
            <div className="space-y-2" data-id="element-120">
              <div data-id="element-121">
                <div className="flex justify-between text-xs text-gray-600 mb-1" data-id="element-122">
                  <span data-id="element-123">Focus</span>
                  <span data-id="element-124">{checkInData?.focus || 3}/5</span>
                </div>
                <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden" data-id="element-125">
                  <div className="h-full bg-gray-600 rounded-full" style={{
                  width: `${(checkInData?.focus || 3) / 5 * 100}%`
                }} data-id="element-126"></div>
                </div>
              </div>
              <div data-id="element-127">
                <div className="flex justify-between text-xs text-gray-600 mb-1" data-id="element-128">
                  <span data-id="element-129">Piekeren</span>
                  <span data-id="element-130">{checkInData?.thoughts || 3}/5</span>
                </div>
                <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden" data-id="element-131">
                  <div className="h-full bg-gray-600 rounded-full" style={{
                  width: `${(checkInData?.thoughts || 3) / 5 * 100}%`
                }} data-id="element-132"></div>
                </div>
              </div>
            </div>
          </div>
          <div className="p-4 border border-gray-300 rounded-lg" data-id="element-133">
            <div className="flex items-center mb-2" data-id="element-134">
              <UsersIcon className="w-4 h-4 text-gray-600 mr-2" data-id="element-135" />
              <h4 className="text-sm font-medium text-gray-700" data-id="element-136">Sociaal</h4>
            </div>
            <div className="space-y-2" data-id="element-137">
              <div data-id="element-138">
                <div className="flex justify-between text-xs text-gray-600 mb-1" data-id="element-139">
                  <span data-id="element-140">Verbinding</span>
                  <span data-id="element-141">{checkInData?.connection || 3}/5</span>
                </div>
                <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden" data-id="element-142">
                  <div className="h-full bg-gray-600 rounded-full" style={{
                  width: `${(checkInData?.connection || 3) / 5 * 100}%`
                }} data-id="element-143"></div>
                </div>
              </div>
              <div data-id="element-144">
                <div className="flex justify-between text-xs text-gray-600 mb-1" data-id="element-145">
                  <span data-id="element-146">Steun</span>
                  <span data-id="element-147">{checkInData?.support || 3}/5</span>
                </div>
                <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden" data-id="element-148">
                  <div className="h-full bg-gray-600 rounded-full" style={{
                  width: `${(checkInData?.support || 3) / 5 * 100}%`
                }} data-id="element-149"></div>
                </div>
              </div>
            </div>
          </div>
          <div className="p-4 border border-gray-300 rounded-lg" data-id="element-150">
            <div className="flex items-center mb-2" data-id="element-151">
              <ActivityIcon className="w-4 h-4 text-gray-600 mr-2" data-id="element-152" />
              <h4 className="text-sm font-medium text-gray-700" data-id="element-153">Fysiek</h4>
            </div>
            <div className="space-y-2" data-id="element-154">
              <div data-id="element-155">
                <div className="flex justify-between text-xs text-gray-600 mb-1" data-id="element-156">
                  <span data-id="element-157">Slaap</span>
                  <span data-id="element-158">{checkInData?.sleep || 3}/5</span>
                </div>
                <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden" data-id="element-159">
                  <div className="h-full bg-gray-600 rounded-full" style={{
                  width: `${(checkInData?.sleep || 3) / 5 * 100}%`
                }} data-id="element-160"></div>
                </div>
              </div>
              <div data-id="element-161">
                <div className="flex justify-between text-xs text-gray-600 mb-1" data-id="element-162">
                  <span data-id="element-163">Energie</span>
                  <span data-id="element-164">{checkInData?.energy || 3}/5</span>
                </div>
                <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden" data-id="element-165">
                  <div className="h-full bg-gray-600 rounded-full" style={{
                  width: `${(checkInData?.energy || 3) / 5 * 100}%`
                }} data-id="element-166"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>;
  };
  return <div className={`w-full max-w-xl mx-auto py-6 space-y-6 transition-opacity duration-500 ${animateIn ? 'opacity-100' : 'opacity-0'}`} data-id="element-167">
      <div className="flex items-center mb-2" data-id="element-168">
        <button onClick={onBackToHome} className="mr-4 p-2 rounded-full hover:bg-gray-100" aria-label="Terug" data-id="element-169">
          <ArrowLeftIcon className="w-5 h-5 text-gray-600" data-id="element-170" />
        </button>
        <h1 className="text-2xl font-light text-gray-800" data-id="element-171">
          Check-in samenvatting
        </h1>
      </div>
      {/* Check-in summary card */}
      <div className="border-2 border-gray-300 rounded-xl overflow-hidden" data-id="element-172">
        <div className="p-6 bg-gray-50" data-id="element-173">
          <div className="flex items-center justify-between mb-6" data-id="element-174">
            <div className="flex items-center" data-id="element-175">
              <CheckCircleIcon className="w-5 h-5 text-gray-700 mr-2" data-id="element-176" />
              <h2 className="text-lg font-medium text-gray-800" data-id="element-177">
                {isQuickCheckIn ? 'Snelle check-in voltooid' : 'Uitgebreide check-in voltooid'}
              </h2>
            </div>
            <div className="text-sm text-gray-600" data-id="element-178">
              {new Date().toLocaleTimeString('nl-NL', {
              hour: '2-digit',
              minute: '2-digit'
            })}
            </div>
          </div>
          <div className="flex items-start mb-6" data-id="element-179">
            <div className="flex-shrink-0 mr-4" data-id="element-180">{getWeatherIcon()}</div>
            <div data-id="element-181">
              <h3 className="text-base font-medium text-gray-800 mb-1" data-id="element-182">
                Jouw mentale weer
              </h3>
              <p className="text-sm text-gray-600 mb-3" data-id="element-183">
                {getWeatherDescription()}
              </p>
              <div className="space-y-3" data-id="element-184">
                <div data-id="element-185">
                  <div className="flex justify-between text-sm text-gray-600 mb-1" data-id="element-186">
                    <span data-id="element-187">Energie</span>
                    <span data-id="element-188">{Math.round(energy)}%</span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-189">
                    <div className="h-full bg-gray-600 rounded-full" style={{
                    width: `${energy}%`
                  }} data-id="element-190"></div>
                  </div>
                </div>
                <div data-id="element-191">
                  <div className="flex justify-between text-sm text-gray-600 mb-1" data-id="element-192">
                    <span data-id="element-193">Positiviteit</span>
                    <span data-id="element-194">{Math.round(positivity)}%</span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-195">
                    <div className="h-full bg-gray-600 rounded-full" style={{
                    width: `${positivity}%`
                  }} data-id="element-196"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {renderDomainInsights()}
          <div className="p-4 border border-gray-200 rounded-lg bg-white" data-id="element-197">
            <div className="flex items-start" data-id="element-198">
              <LightbulbIcon className="w-5 h-5 text-gray-600 mt-0.5 mr-3 flex-shrink-0" data-id="element-199" />
              <div data-id="element-200">
                <p className="text-sm text-gray-700 font-medium mb-1" data-id="element-201">
                  Inzicht
                </p>
                <p className="text-sm text-gray-600" data-id="element-202">{getCheckInInsight()}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Recommended exercises */}
      <div className="border-2 border-gray-300 rounded-xl p-5" data-id="element-203">
        <div className="flex items-center mb-4" data-id="element-204">
          <SparklesIcon className="w-5 h-5 text-gray-700 mr-2" data-id="element-205" />
          <h2 className="text-lg font-medium text-gray-800" data-id="element-206">
            Aanbevolen voor je huidige stemming
          </h2>
        </div>
        <div className="space-y-3" data-id="element-207">
          {getRecommendedExercises().map(exercise => <button key={exercise.id} className="w-full p-4 text-left border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" onClick={() => onStartExercise(exercise.id)} data-id="element-208">
              <h3 className="text-base font-medium text-gray-800 mb-1" data-id="element-209">
                {exercise.title}
              </h3>
              <p className="text-sm text-gray-600" data-id="element-210">{exercise.description}</p>
            </button>)}
        </div>
      </div>
      {/* Action buttons */}
      <div className="flex justify-between pt-2" data-id="element-211">
        <Button variant="secondary" onClick={onBackToHome} leftIcon={<ArrowLeftIcon className="w-4 h-4" data-id="element-213" />} data-id="element-212">
          Terug naar home
        </Button>
        <Button onClick={onViewSuggestions} rightIcon={<ArrowRightIcon className="w-4 h-4" data-id="element-215" />} data-id="element-214">
          Meer suggesties
        </Button>
      </div>
    </div>;
};
export default CheckInSummary;